import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { FetchParams } from './employeesSlice';
import { buildQueryString } from '../../utils/queryStringBuilder';

interface ProjectsState {
    projects: any[];
    project: any | null;
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}

const initialState: ProjectsState = {
    projects: [],
    project: null,
    isLoading: false,
    error: null,
    pagination: {
        page: 0,
        pageSize: 10,
        total: 0
    }
};

export const fetchProjects = createAsyncThunk(
    'projects/fetchProjects',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            // Build query string using the utility function
            const queryString = buildQueryString(params);
            
            const response = await api.get(`/api/projects${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch projects');
        }
    }
);

export const fetchProject = createAsyncThunk(
    'projects/fetchProject',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/projects/${id}?populate=*`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch project');
        }
    }
);

export const createProject = createAsyncThunk(
    'projects/createProject',
    async (projectData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/projects', { data: projectData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create project');
        }
    }
);

export const updateProject = createAsyncThunk(
    'projects/updateProject',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/projects/${id}`, { data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update project');
        }
    }
);

export const deleteProject = createAsyncThunk(
    'projects/deleteProject',
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/projects/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete project');
        }
    }
);

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        clearProjectError: (state) => {
            state.error = null;
        },
        clearProject: (state) => {
            state.project = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Projects
            .addCase(fetchProjects.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProjects.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.projects = action.payload.data;
                if (action.payload.meta && action.payload.meta.pagination) {
                    state.pagination = {
                        page: action.payload.meta.pagination.page - 1, // Convert to 0-based for frontend
                        pageSize: action.payload.meta.pagination.pageSize,
                        total: action.payload.meta.pagination.total
                    };
                }
            })
            .addCase(fetchProjects.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Project
            .addCase(fetchProject.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProject.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.project = action.payload.data;
            })
            .addCase(fetchProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create Project
            .addCase(createProject.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createProject.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.projects.push(action.payload.data);
            })
            .addCase(createProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update Project
            .addCase(updateProject.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateProject.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                const index = state.projects.findIndex(project => project.id === action.payload.data.id);
                if (index !== -1) {
                    state.projects[index] = action.payload.data;
                }
                state.project = action.payload.data;
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete Project
            .addCase(deleteProject.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteProject.fulfilled, (state, action: PayloadAction<number>) => {
                state.isLoading = false;
                state.projects = state.projects.filter(project => project.id !== action.payload);
                if (state.project && state.project.id === action.payload) {
                    state.project = null;
                }
            })
            .addCase(deleteProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearProjectError, clearProject } = projectsSlice.actions;
export default projectsSlice.reducer; 